/*
 * localStorage-backed persistence for the mock API.
 * Each collection is a JSON array under a namespaced key. All public API
 * functions (trips.ts, points.ts, auth.ts) return Promises and simulate a
 * small latency, so the surface matches a real HTTP client and can be swapped
 * without touching React Query hooks or UI.
 */

const NS = 'smart-travel'

export const STORE_KEYS = {
  users: `${NS}:users`,
  trips: `${NS}:trips`,
  points: `${NS}:points`,
  places: `${NS}:places`,
  places_deleted: `${NS}:places-deleted`,
  notes: `${NS}:notes`,
  participants: `${NS}:participants`,
  media: `${NS}:media`,
  documents: `${NS}:documents`,
  recommendations: `${NS}:recommendations`,
  expenses: `${NS}:expenses`,
  links: `${NS}:links`,
  session: `${NS}:session`,
  seeded: `${NS}:seeded`,
} as const

export type StoreKey = (typeof STORE_KEYS)[keyof typeof STORE_KEYS]

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined'
  } catch {
    return false
  }
}

export function readCollection<T>(key: StoreKey): T[] {
  if (!hasStorage()) return []
  const raw = localStorage.getItem(key)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

export function writeCollection<T>(key: StoreKey, items: T[]): void {
  if (!hasStorage()) return
  localStorage.setItem(key, JSON.stringify(items))
}

export function readValue<T>(key: StoreKey): T | null {
  if (!hasStorage()) return null
  const raw = localStorage.getItem(key)
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export function writeValue<T>(key: StoreKey, value: T | null): void {
  if (!hasStorage()) return
  if (value === null) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, JSON.stringify(value))
}

/** Simulate network latency so loading states are exercised in the UI. */
export function latency<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms))
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}
